<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useSlots, watch } from 'vue';
import {
  VIDEO_PROVIDER_PRECONNECT_ORIGINS,
  VideoEmbedVariant,
  VideoProvider,
  fetchVimeoThumbnailUrl,
  getNextYoutubeThumbnailQuality,
  getVideoEmbedUrl,
  getVideoProvider,
  getYoutubeThumbnailUrl,
  getYoutubeVideoId,
  withVideoAutoplay,
  withVimeoDoNotTrack,
  type YoutubeThumbnailQuality,
} from '@pkg/utilities/video';
import { VideoType } from '@pkg/sanity-utils/constants/video';
import { SITE_IDS } from '@pkg/config/sanity/audience';
import { logger } from '@pkg/logger';
import { buildAbsoluteSiteUrl, normalizePublicOrigin } from '@pkg/link/src/lib/crossSiteLink';
import { useI18n } from 'vue-i18n';
import { useCookieConsent } from '@pkg/ui/composables/useCookieConsent';

interface Props {
  url: string;
  webm?: string;
  ogg?: string;
  title?: string;
  controls?: boolean;
  autoplay?: boolean;
  videoType?: VideoType;
  /**
   * Embed the player on load instead of showing the click-to-play facade.
   * Only for videos that must be running before any interaction.
   */
  eager?: boolean;
  /** Poster image URL; overrides the provider thumbnail. The `cover` slot wins over both. */
  poster?: string;
}

const props = withDefaults(defineProps<Props>(), {
  title: 'Video player',
});

const { t } = useI18n();
const localePath = useLocalePath();
const runtimeConfig = useRuntimeConfig();

/**
 * Legal pages, including privacy settings, are served by the consumer site.
 * On the business app this has to be an absolute www URL.
 */
const privacySettingsPath = computed(() => {
  const path = localePath('/legal/privacy-settings');
  const publicConfig = runtimeConfig.public as {
    siteId?: string;
    wwwDomain?: string;
  };

  if (publicConfig.siteId !== SITE_IDS.BUSINESS) {
    return path;
  }

  const wwwOrigin = normalizePublicOrigin(publicConfig.wwwDomain);
  if (!wwwOrigin) {
    logger.warn(
      'Privacy settings link on the business site requires NUXT_PUBLIC_WWW_DOMAIN; falling back to a same-origin path',
    );
    return path;
  }

  return buildAbsoluteSiteUrl(wwwOrigin, path);
});

const { functional: hasFunctionalConsent } = useCookieConsent('functional');

const slots = useSlots();

const provider = computed(() => getVideoProvider(props.url));

// Vimeo embeds may only load once functional-cookie consent is granted.
const isVimeo = computed(() => provider.value === VideoProvider.Vimeo);
const isConsentGated = computed(() => isVimeo.value && !hasFunctionalConsent.value);

/** Set once the visitor has asked for the player, which is what injects the iframe. */
const isActivated = ref(false);

/**
 * Authored autoplay can never fire behind a facade (it needs a click), so those
 * videos keep embedding on load. Everything else stays a static thumbnail until
 * clicked, which keeps the third-party player entirely off the initial page load.
 */
const isFacadeMode = computed(() => !props.eager && !props.autoplay);

const showPlayer = computed(
  () => isActivated.value || (!isFacadeMode.value && !isConsentGated.value),
);

const embedSrc = computed(() => {
  const url = withVimeoDoNotTrack(
    getVideoEmbedUrl({
      videoUrl: props.url,
      variant: VideoEmbedVariant.Inline,
      controls: props.controls ?? true,
      autoplay: props.autoplay ?? false,
    }),
  );

  // A click on the facade is a request to play, so honour it.
  return isActivated.value ? withVideoAutoplay(url) : url;
});

const youtubeVideoId = computed(() =>
  provider.value === VideoProvider.Youtube ? getYoutubeVideoId(props.url) : null,
);

const youtubeThumbnailQuality = ref<YoutubeThumbnailQuality>('maxresdefault');
const vimeoThumbnailUrl = ref<string | null>(null);

const resolvedPoster = computed(() => {
  if (props.poster) {
    return props.poster;
  }

  if (youtubeVideoId.value) {
    return getYoutubeThumbnailUrl(youtubeVideoId.value, youtubeThumbnailQuality.value);
  }

  return vimeoThumbnailUrl.value;
});

/** Fall back through the quality list when YouTube has no image at this size. */
const onPosterError = () => {
  if (!youtubeVideoId.value) {
    return;
  }

  const nextQuality = getNextYoutubeThumbnailQuality(youtubeThumbnailQuality.value);

  if (nextQuality) {
    youtubeThumbnailQuality.value = nextQuality;
  }
};

let thumbnailRequest: AbortController | undefined;

/**
 * The Vimeo thumbnail needs an oEmbed lookup, which itself contacts Vimeo — so it
 * waits for the same gate the Vimeo embed already waits for. Until then the `cover`
 * slot (a CMS-authored fallback image) or a plain placeholder is shown.
 */
const resolveVimeoThumbnail = () => {
  if (
    provider.value !== VideoProvider.Vimeo
    || props.poster
    || vimeoThumbnailUrl.value
    || isConsentGated.value
  ) {
    return;
  }

  thumbnailRequest?.abort();
  thumbnailRequest = new AbortController();

  fetchVimeoThumbnailUrl({ videoUrl: props.url, signal: thumbnailRequest.signal }).then((url) => {
    vimeoThumbnailUrl.value = url;
  });
};

const showConsentMessage = ref(false);

const onPlayClick = () => {
  // Gated videos point at the privacy settings instead of playing, as before.
  if (isConsentGated.value) {
    showConsentMessage.value = true;
    return;
  }

  isActivated.value = true;
};

watch(hasFunctionalConsent, (granted) => {
  if (!granted) {
    return;
  }

  showConsentMessage.value = false;
  resolveVimeoThumbnail();
});

/** Warm up the player's connections on hover/focus, before the click lands. */
const isIntentSignalled = ref(false);
const onPlayIntent = () => {
  isIntentSignalled.value = true;
};

useHead(() => {
  const currentProvider = provider.value;

  if (!currentProvider || !isIntentSignalled.value || isActivated.value || isConsentGated.value) {
    return {};
  }

  return {
    link: VIDEO_PROVIDER_PRECONNECT_ORIGINS[currentProvider].map((href) => ({
      rel: 'preconnect',
      href,
      id: href,
    })),
  };
});

const nativeControls = computed(() => props.controls ?? true);
const nativeMuted = computed(() => !nativeControls.value);
const nativeAutoplay = computed(() => (nativeControls.value ? false : props.autoplay ?? true));

const videoRef = ref<HTMLVideoElement | null>(null);
onMounted(() => {
  if (videoRef.value) {
    videoRef.value.muted = nativeMuted.value;
  }

  resolveVimeoThumbnail();
});

onBeforeUnmount(() => {
  thumbnailRequest?.abort();
});
</script>

<template>
  <div v-if="videoType === VideoType.HOSTED" class="c-video">
    <video
      ref="videoRef"
      :autoplay="nativeAutoplay"
      :muted="nativeMuted"
      :controls="nativeControls"
      :preload="nativeAutoplay ? 'auto' : 'metadata'"
      loop
      playsinline
    >
      <source :src="url" type="video/mp4" />
      <source v-if="webm" :src="webm" type="video/webm">
      <source v-if="ogg" :src="ogg" type="video/ogg">
      Your browser does not support the video tag
    </video>
  </div>
  <div v-else-if="showConsentMessage" class="c-video c-video--gated">
    <div class="c-video__consent">
      <p class="c-video__consent-text">
        {{ t('videoConsentRequired') }}
        <NuxtLink :to="privacySettingsPath" class="c-video__consent-link">
          {{ t('videoConsentRequiredLink') }}
        </NuxtLink>
      </p>
    </div>
  </div>
  <div v-else-if="!showPlayer" class="c-video c-video--facade">
    <button
      type="button"
      class="c-video__placeholder"
      :aria-label="title"
      @click="onPlayClick"
      @pointerenter="onPlayIntent"
      @focus="onPlayIntent"
    >
      <slot name="cover" />
      <img
        v-if="!slots.cover && resolvedPoster"
        :src="resolvedPoster"
        :alt="title"
        class="c-video__poster"
        loading="lazy"
        decoding="async"
        @error="onPosterError"
      >
      <span class="c-video__play" aria-hidden="true">
        <svg viewBox="0 0 68 68" width="68" height="68" focusable="false">
          <circle cx="34" cy="34" r="34" fill="rgba(0, 0, 0, 0.6)" />
          <path d="M27 22l22 12-22 12V22z" fill="#fff" />
        </svg>
      </span>
    </button>
  </div>
  <div v-else-if="embedSrc" class="c-video">
    <iframe
      :src="embedSrc"
      :title="title"
      allow="autoplay; encrypted-media; gyroscope; picture-in-picture; web-share"
      referrerpolicy="strict-origin-when-cross-origin"
      loading="lazy"
    />
  </div>
</template>

<style scoped>
.c-video {
  aspect-ratio: 16 / 9;
  width: 100%;
  margin: 0 auto;
  display: grid;
  place-items: center;

  @screen tablet-wide {
    max-width: 1010px;
  }

  & iframe {
    width: 100%;
    height: 100%;
  }

  & video {
    max-width: 100%;
    max-height: 100%;
  }
}

.c-video--facade,
.c-video--gated {
  position: relative;
  overflow: hidden;
  background-color: #1c1c1c;
}

.c-video__placeholder {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  cursor: pointer;
  background: transparent;
}

.c-video__placeholder :deep(img),
.c-video__poster {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.c-video__play {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  transition: transform 0.15s ease;
}

.c-video__placeholder:hover .c-video__play,
.c-video__placeholder:focus-visible .c-video__play {
  transform: translate(-50%, -50%) scale(1.08);
}

.c-video__consent {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: theme('spacing.24');
  text-align: center;
}

.c-video__consent-text {
  color: #fff;
  max-width: 42ch;
}

.c-video__consent-link {
  text-decoration: underline;
}
</style>
